import { useEffect, useState } from "react"

type TQueryState<TData> = {
  data: TData | null
  error: unknown
  isLoading: boolean
}

/**
 * Runs `queryFn` on mount and whenever it (or the external signal) changes,
 * owning the loading/error/data state so components don't have to.
 *
 * The hook supplies its own AbortSignal to `queryFn` and aborts it on cleanup,
 * so an in-flight request is cancelled when the component unmounts. A caller
 * can pass an additional `signal` to tie the request to an outer scope; both
 * are composed, so either one cancelling aborts the request.
 */
export function useQuery<TData>(
  queryFn: (context: { signal: AbortSignal }) => Promise<TData>,
  options?: { signal?: AbortSignal }
) {
  const [state, setState] = useState<TQueryState<TData>>({
    data: null,
    error: null,
    isLoading: true,
  })

  const externalSignal = options?.signal

  useEffect(() => {
    const controller = new AbortController()
    const signal = externalSignal
      ? AbortSignal.any([controller.signal, externalSignal])
      : controller.signal

    setState((prev) => ({ ...prev, isLoading: true }))

    queryFn({ signal })
      .then((data) => {
        if (signal.aborted) {
          return
        }
        setState({ data, error: null, isLoading: false })
      })
      .catch((error: unknown) => {
        if (signal.aborted) {
          return
        }
        setState({ data: null, error, isLoading: false })
      })

    return () => controller.abort()
  }, [queryFn, externalSignal])

  return { ...state, isError: state.error !== null }
}
